import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = (url) => axios.get(`${API_URL}${url}`, { withCredentials: true }).catch(() => axios.get(`${API_URL}${url}`));

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PRIORITY_DOT = { red: '#DC2626', orange: '#F59E0B', green: '#10B981' };

const AnnualPlanner = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState({ months: {} });
  const [loading, setLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api(`/api/opportunities/planner?year=${year}`)
      .then(r => setData(r.data))
      .catch(() => setData({ months: {} }))
      .finally(() => setLoading(false));
  }, [year]);

  const currentMonth = new Date().getMonth() + 1;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAF6', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="annual-planner">
      <nav className="bg-white border-b px-6 py-3" style={{ borderColor: '#E8E0D4' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 no-underline">
              <img src="/maluti-logo-web.png" alt="Maluti" className="h-7 object-contain" />
              <span className="text-xs font-medium" style={{ color: '#2C1A0E' }}>Ops Suite</span>
            </Link>
            <span className="text-xs" style={{ color: '#D1D5DB' }}>/</span>
            <Link to="/bi-dashboard" className="text-xs no-underline hover:underline" style={{ color: '#8B7355' }}>BI</Link>
            <span className="text-xs" style={{ color: '#D1D5DB' }}>/</span>
            <span className="text-xs font-semibold" style={{ color: '#B8860B' }}>Annual Planner</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/bi-dashboard" className="text-[10px] uppercase tracking-wider font-medium no-underline hover:underline" style={{ color: '#8B7355' }}>Dashboard</Link>
            <Link to="/opportunities" className="text-[10px] uppercase tracking-wider font-medium no-underline hover:underline" style={{ color: '#8B7355' }}>Opportunities</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Year Selector */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-light" style={{ color: '#2C1A0E' }}>Annual Commercial Planner</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => setYear(y => y - 1)} className="p-1.5 rounded hover:bg-amber-50"><ChevronLeft className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            <span className="text-lg font-light min-w-[60px] text-center" style={{ color: '#2C1A0E' }}>{year}</span>
            <button onClick={() => setYear(y => y + 1)} className="p-1.5 rounded hover:bg-amber-50"><ChevronRight className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
          </div>
        </div>

        {/* 12-Month Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {MONTH_NAMES.map((monthName, idx) => {
            const monthKey = String(idx + 1).padStart(2, '0');
            const monthOpps = data.months?.[monthKey] || [];
            const isCurrentMonth = year === new Date().getFullYear() && idx + 1 === currentMonth;
            const isExpanded = expandedMonth === monthKey;
            const redCount = monthOpps.filter(o => o.priority === 'red').length;
            const orangeCount = monthOpps.filter(o => o.priority === 'orange').length;
            const totalRevenue = monthOpps.reduce((sum, o) => sum + (o.estimated_revenue || 0), 0);

            return (
              <div
                key={monthKey}
                className={`bg-white border rounded-sm transition-all hover:shadow-md ${isExpanded ? 'col-span-1 sm:col-span-2' : 'cursor-pointer'}`}
                style={{ borderColor: isCurrentMonth ? '#D4AF37' : '#E8E0D4', borderWidth: isCurrentMonth ? '2px' : '1px' }}
                onClick={() => { if (!isExpanded) setExpandedMonth(monthKey); }}
                data-testid={`month-${monthKey}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium" style={{ color: isCurrentMonth ? '#B8860B' : '#2C1A0E' }}>
                      {monthName}
                      {isCurrentMonth && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#D4AF37', color: '#FFF' }}>NOW</span>}
                    </h3>
                    <span className="text-xs font-semibold" style={{ color: monthOpps.length > 0 ? '#2C1A0E' : '#D1D5DB' }}>{monthOpps.length}</span>
                  </div>

                  {/* Priority dots summary */}
                  {monthOpps.length > 0 && (
                    <div className="flex items-center gap-3 mb-2">
                      {redCount > 0 && <span className="text-[9px] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#DC2626' }} />{redCount}</span>}
                      {orangeCount > 0 && <span className="text-[9px] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} />{orangeCount}</span>}
                      {totalRevenue > 0 && <span className="text-[9px] font-medium" style={{ color: '#B8860B' }}>£{totalRevenue.toLocaleString()}</span>}
                    </div>
                  )}

                  {/* Collapsed: show first 3 */}
                  {!isExpanded && monthOpps.slice(0, 3).map(o => (
                    <div key={o.id} className="flex items-center gap-2 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PRIORITY_DOT[o.priority] || '#10B981' }} />
                      <span className="text-[10px] truncate" style={{ color: '#4A3A2E' }}>{o.name}</span>
                    </div>
                  ))}
                  {!isExpanded && monthOpps.length > 3 && (
                    <p className="text-[9px] mt-1" style={{ color: '#B8860B' }}>+{monthOpps.length - 3} more</p>
                  )}
                  {monthOpps.length === 0 && !isExpanded && (
                    <p className="text-[10px] py-2" style={{ color: '#D1D5DB' }}>No opportunities</p>
                  )}
                </div>

                {/* Expanded view */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: '#E8E0D4' }}>
                    <div className="flex items-center justify-between mt-3 mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#8B7355' }}>{monthOpps.length} opportunities</span>
                      <button onClick={(e) => { e.stopPropagation(); setExpandedMonth(null); }} className="text-[10px] px-2 py-1 rounded hover:bg-amber-50" style={{ color: '#B8860B' }}>Collapse</button>
                    </div>
                    <div className="flex flex-col gap-2">
                      {monthOpps.map(o => (
                        <div key={o.id} className="flex items-center justify-between p-2 rounded-sm hover:bg-amber-50" onClick={(e) => { e.stopPropagation(); navigate(`/opportunities?id=${o.id}`); }}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PRIORITY_DOT[o.priority] || '#10B981' }} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate" style={{ color: '#2C1A0E' }}>{o.name}</p>
                              <div className="flex items-center gap-2">
                                {o.category && <span className="text-[9px]" style={{ color: '#8B7355' }}>{o.category}</span>}
                                {o.city && <span className="text-[9px]" style={{ color: '#8B7355' }}>{o.city}</span>}
                                {o.date && <span className="text-[9px]" style={{ color: '#8B7355' }}>{new Date(o.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {o.estimated_revenue > 0 && <span className="text-[10px] font-semibold" style={{ color: '#B8860B' }}>£{o.estimated_revenue.toLocaleString()}</span>}
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full capitalize" style={{ backgroundColor: '#F5F0E8', color: '#6B5B4E' }}>{o.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); navigate('/opportunities?new=1'); }} className="mt-3 text-[10px] underline" style={{ color: '#B8860B' }}>+ Add opportunity to {monthName}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AnnualPlanner;
